import type { CSSProperties } from 'react';

import { useDraggable } from '@dnd-kit/react';
import { type VIEW_KEY } from '@repo/types/enums/viewKey';
import { type MeetingPublic } from '@repo/types/meetings';
import { differenceInDays } from 'date-fns';
import { useRef, useState } from 'react';

import { EventItem } from '@/components/features/calendar';
import { Badge } from '@/components/ui/badge';
import { getCalendarEventUniqueId } from '@/utils/calendar';

interface DraggableEventProps {
  event: MeetingPublic;
  view: VIEW_KEY;
  showTime?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  height?: number;
  isMultiDay?: boolean;
  multiDayWidth?: number;
  isFirstDay?: boolean;
  isLastDay?: boolean;
  'aria-hidden'?: boolean | 'true' | 'false';
}

export function DraggableEvent(draggableEventProps: DraggableEventProps) {
  const { event, view, showTime, disabled = false, onClick, height, isMultiDay, multiDayWidth, isFirstDay = true, isLastDay = true, 'aria-hidden': ariaHidden } = draggableEventProps;
  const elementRef = useRef<HTMLDivElement>(null);
  const [dragHandlePosition, setDragHandlePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Check if this is a multi-day event
  const eventStart = new Date(event.startDate);
  const eventEnd = new Date(event.endDate);
  const isMultiDayEvent = isMultiDay || event.allDay || differenceInDays(eventEnd, eventStart) >= 1;

  const { ref: setNodeRef, isDragging } = useDraggable({
    id: `${getCalendarEventUniqueId(event)}-${view}`,
    disabled,
    data: {
      event,
      view,
      height: height || elementRef.current?.offsetHeight || null,
      isMultiDay: isMultiDayEvent,
      multiDayWidth: multiDayWidth,
      dragHandlePosition,
      isFirstDay,
      isLastDay,
    },
  });

  // Handle mouse down to track where on the event the user clicked
  const handleMouseDown = (e: React.MouseEvent) => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setDragHandlePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  if (isDragging) {
    return (
      <div ref={setNodeRef} className="bg-surface-secondary/50 border-border-secondary flex items-center justify-center rounded-xs border-3 p-0.5" style={{ height: height || 'auto' }}>
        <p className="text-text-tertiary text-xs">
          Naciśnij{' '}
          <Badge variant={'muted'} size={'sm'} className="px-1 text-xs">
            ESC
          </Badge>
          , aby anulować
        </p>
      </div>
    );
  }

  const style: CSSProperties = {
    height: height || 'auto',
    width: isMultiDayEvent && multiDayWidth ? `${multiDayWidth}%` : undefined,
  };

  // Handle touch start to track where on the event the user touched
  const handleTouchStart = (e: React.TouchEvent) => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      if (!touch) return;
      setDragHandlePosition({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    }
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        elementRef.current = node;
      }}
      style={style}
      className="touch-none"
    >
      <EventItem
        event={event}
        view={view}
        showTime={showTime}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        isDragging={isDragging}
        onClick={onClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        aria-hidden={ariaHidden}
      />
    </div>
  );
}
